import React from "react";
import { Box, Tooltip } from "@mui/material";
import { AllInclusive, CheckCircle, HelpRounded, ShoppingCart } from "@mui/icons-material";
import { TimeSlotDto } from "../api-client";
import { MobiliAvatar } from "../atoms/MobiliAvatar";
import { VisitTypeBadge } from "../atoms/Chip";
import { InformingBadge } from "../atoms/InformingBadge";
import { getColorForRxNumber } from "../helpers/statusColor";
import { formatDateUI, formatTime } from "../helpers/dateFormat";
import { slotTherapy } from "../helpers/slotTherapy";
import { mobiliTheme } from "../themes/mobiliTheme";
import { AppointmentTime } from "./AppointmentTime";

export interface TherapySlotRowProps {
  timeSlot: TimeSlotDto;
  appointment?: any;
  /** Position within the appointment's slots — only used to place the reason line. */
  index?: number;
  totalSlots?: number;
  /** Show the slot's own start/end times above the row (scheduler drag targets). */
  displayTimeslotTime?: boolean;
  /**
   * Drop the `n/total` treatment index from the progress slot. The planning
   * shelf's unscheduled cards ask "how many of these are stacked here?", which
   * their own count badge answers; the per-slot fraction next to it read as a
   * second, contradictory count. Perpetual Rxs keep the infinity — that is a
   * property of the Rx, not a position within it.
   */
  hideTreatmentIndex?: boolean;
  /**
   * Replaces the trailing attended/informed symbol. The attendance dialog puts
   * an interactive toggle in that slot instead of a read-only status icon.
   */
  trailing?: React.ReactNode;
}

type AttendedStatus = "future" | "unattended" | "attended" | "unscheduled";

/**
 * Column pitch for the trailing symbols, and the glyph size inside it. Tokens
 * rather than constants so a container can declare `data-density="compact"`
 * and tighten the whole row without this component knowing where it renders.
 */
const ICON_SLOT = "var(--row-slot)";

/**
 * Floor for the shared treatment-index / perpetual slot, rather than a fixed
 * width: reserving the worst case ("10/10", 32px) on every row spent ~14px of
 * centring slack next to a typical "1/6", and in the Rx Termine card that
 * slack is the difference between the therapy fitting and being clipped.
 * Alignment across the sibling rows of one appointment comes from the hidden
 * sizer below, not from this floor — the floor only keeps the slot from
 * collapsing when no row in the appointment carries an index at all.
 */
const PROGRESS_SLOT_MIN = "var(--row-progress-min)";

/**
 * The index a slot actually displays in the progress slot: perpetual Rxs show
 * the infinity instead, so their fraction must not influence sizing either.
 */
const displayedIndex = (ts?: TimeSlotDto): string =>
  ts?.therapyRx?.rx?.isPerpetual ? "" : ts?.treatmentIndex ?? "";

/**
 * Nickname size for the 18px avatar (15px inside the ring).
 *
 * Two-character initials are the normal case and get 10px — a larger share of
 * the circle than the old 0.8em-in-25px, so the nickname reads bigger even
 * though the avatar shrank. `nickName` is an unconstrained varchar though, so
 * longer values do occur; they step down rather than clipping mid-glyph.
 */
const nicknameFontSize = (nickName?: string | null): string =>
  (nickName?.length ?? 0) <= 2 ? "10px" : "8px";

/**
 * MUI's icons default to 24px, which is twice the type size on this row and was
 * what actually set its height — the chips were never the constraint. The atoms
 * that render them (VisitTypeBadge, MobiliAvatar) are shared with roomier
 * surfaces, so the size is imposed here from the parent rather than changed at
 * the source.
 */
const denseIcons = { "& .MuiSvgIcon-root": { fontSize: "var(--row-icon)" } };

/**
 * One time slot of an appointment: Rx number, treatment index, therapy,
 * therapist, and the trailing status symbols.
 *
 * The Rx number is colour-coded, but the colour lives in a rail down the left
 * edge rather than a chip background — the same 4px accent border `.mc-event`
 * uses in MobiliCalendar (design-system rule 4: tint the edge, not the fill).
 * Consecutive slots on the same Rx merge into one continuous bar, which is the
 * actual question being asked of this row ("same Rx, or a different one?").
 * Therapy and treatment index carry no colour at all.
 */
export const TherapySlotRow: React.FC<TherapySlotRowProps> = ({
  timeSlot,
  appointment,
  index,
  totalSlots,
  displayTimeslotTime,
  hideTreatmentIndex,
  trailing,
}) => {
  const rxNumber = timeSlot?.therapyRx?.rx?.rxNumber || "";
  const railColor = rxNumber ? getColorForRxNumber(rxNumber) : "transparent";
  const therapyAbbreviation = (slotTherapy(timeSlot) as any)?.abbreviation;

  /*
    Widest index among the appointment's slots, so every row of one appointment
    reserves the same progress-slot width and the columns line up ("1/1" next
    to "5/10"). Longest string is widest string: the digits are `tabular-nums`
    and each index has exactly one slash. Rows across different appointments
    may still differ — only intra-appointment alignment is wanted, and paying
    the global worst case would spend width these dense cards do not have.
  */
  const ownIndex = displayedIndex(timeSlot);
  const widestIndex = ((appointment?.timeSlots ?? []) as TimeSlotDto[]).reduce(
    (widest, ts) => {
      const idx = displayedIndex(ts);
      return idx.length > widest.length ? idx : widest;
    },
    ownIndex
  );

  const attendedStatus: AttendedStatus = !timeSlot.start
    ? "unscheduled"
    : timeSlot.attended
    ? "attended"
    : new Date(timeSlot.start) > new Date()
    ? "future"
    : "unattended";

  const showReason =
    appointment?.reason &&
    !appointment?.end &&
    index !== undefined &&
    totalSlots !== undefined &&
    index === totalSlots - 1;

  return (
    <div className="w-full">
      {displayTimeslotTime && timeSlot && (
        <div className="text-right">
          <AppointmentTime
            className="text-mini font-semibold text-ink-muted tabular-nums"
            start={timeSlot.start}
            end={timeSlot.end}
          />
        </div>
      )}

      {/*
        No vertical padding and square corners, so adjacent rows on the same Rx
        read as one uninterrupted rail.

        `border-solid` is not optional: preflight is disabled (tailwind.config.js,
        so Tailwind does not wreck MUI), which means Tailwind's base
        `border-style: solid` reset never lands and a bare `border-l-4` renders
        nothing at all.

        Width priority is set by the narrowest container — the patient drawer is
        320px, leaving this row about 210px. The trailing symbols never shrink
        (they are the flags, and a clipped flag is a wrong flag) and neither does
        the Rx number (it is the row's identity); the treatment index and therapy
        give up their reserved width first and then ellipsize. Everything was
        `shrink-0` before, so overflow had nowhere to go but out of the card,
        which pushed the attended symbol past its right edge.
      */}
      <div
        className="flex min-w-0 items-center gap-1 border-0 border-l-4 border-solid pl-1.5 rounded-none"
        style={{ borderLeftColor: railColor }}
      >
        {rxNumber ? (
          <Tooltip title={`Rezept ${rxNumber}`}>
            <span className="shrink-0 text-title font-semibold text-ink-text tabular-nums whitespace-nowrap">
              {rxNumber}
            </span>
          </Tooltip>
        ) : (
          <Box sx={{ ...denseIcons, flexShrink: 0, display: "flex" }}>
            <ShoppingCart />
          </Box>
        )}

        {/*
          Therapy is the one column that grows: it holds the longest values
          ("KGN PNF"), so all slack in the row belongs to it. There is
          deliberately no spacer element after this — a `flex-1` spacer would win
          the free space and leave the therapy clipped with visible empty room to
          its right.

          Clipped, NOT ellipsised (so `overflow-hidden` rather than Tailwind's
          `truncate`). An ellipsis costs about 5px and, worse, makes the browser
          drop whole characters to fit itself — roughly two characters of a
          therapy code spent to report that characters are missing. At this width
          there is never room to spend. The full value is on the tooltip, and a
          partial code is far more identifiable than a shorter one plus dots.
        */}
        <Tooltip title={therapyAbbreviation || ""}>
          <span className="min-w-0 flex-1 overflow-hidden whitespace-nowrap text-dense font-semibold text-ink-text">
            {therapyAbbreviation || ""}
          </span>
        </Tooltip>

        {/* Therapist and the three status symbols share one cluster so they sit
            on the same 18px column pitch and cost one gap between them, not
            four. In a 320px drawer those gaps are the difference between
            fitting and pushing the attended symbol out of the card. */}
        <Box sx={{ ...denseIcons, flexShrink: 0, display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: ICON_SLOT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {timeSlot?.user?.id && (
              // MobiliAvatar's default 0.2rem ring sits OUTSIDE the given size
              // (content-box) and carries a decorative drop shadow, which is
              // what actually held this row at 24px. Both are overridden here
              // rather than on the atom, which roomier surfaces still want.
              // Sized in px, not em: MobiliAvatar forwards sx.fontSize straight
              // to the inner Typography, so an em value would resolve against
              // whatever surface the row landed on.
              <MobiliAvatar
                user={timeSlot.user}
                isTherapist
                sx={{
                  width: ICON_SLOT,
                  height: ICON_SLOT,
                  fontSize: nicknameFontSize(timeSlot.user.nickName),
                  fontWeight: 700,
                  lineHeight: 1,
                  boxSizing: "border-box",
                  borderWidth: "1.5px",
                  boxShadow: "none",
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              width: ICON_SLOT,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <VisitTypeBadge
              visitType={appointment?.address?.type}
              visitFee={timeSlot?.visitFee?.abbreviation}
            />
          </Box>
          {/*
            One slot for two mutually exclusive facts. `treatmentIndex` is
            written as `n/total` where total is how many treatments currently
            exist for the therapyRx — on a perpetual Rx that denominator is not
            a prescribed amount and grows as you schedule, so the fraction is
            meaningless there. The infinity says the same thing truthfully.
            Sharing one slot therefore costs no information and returns a whole
            column's width to the therapy.
          */}
          {/*
            Grid, not flex: the hidden sizer and the visible content stack in
            the same cell, so the sizer contributes width but no height and the
            content stays centred in whatever width wins. The sizer is only
            rendered when a SIBLING's index is wider than this row's own —
            otherwise the visible content is already the widest and would only
            be duplicated in the DOM.
          */}
          {/* With the index suppressed the slot has nothing left to show on a
              non-perpetual Rx, so it is dropped entirely rather than reserving
              PROGRESS_SLOT_MIN of empty column on every row. */}
          {(!hideTreatmentIndex || timeSlot.therapyRx?.rx?.isPerpetual) && (
            <Box
              sx={{
                minWidth: PROGRESS_SLOT_MIN,
                display: "grid",
                justifyItems: "center",
                alignItems: "center",
                "& > *": { gridArea: "1 / 1" },
              }}
            >
              {!hideTreatmentIndex && widestIndex.length > ownIndex.length && (
                <span
                  aria-hidden
                  className="whitespace-nowrap text-dense tabular-nums"
                  style={{ visibility: "hidden" }}
                >
                  {widestIndex}
                </span>
              )}
              {timeSlot.therapyRx?.rx?.isPerpetual ? (
                <Tooltip title="Dauerrezept">
                  <AllInclusive />
                </Tooltip>
              ) : (
                timeSlot.treatmentIndex && (
                  <Tooltip title={`Behandlung ${timeSlot.treatmentIndex}`}>
                    <span className="whitespace-nowrap text-dense text-ink-muted tabular-nums">
                      {timeSlot.treatmentIndex}
                    </span>
                  </Tooltip>
                )
              )}
            </Box>
          )}
          <Box
            sx={{
              width: ICON_SLOT,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {trailing !== undefined && trailing}
            {trailing === undefined && attendedStatus === "attended" && (
              <CheckCircle sx={{ color: mobiliTheme.palette.success.main }} />
            )}
            {trailing === undefined && attendedStatus === "unattended" && (
              <HelpRounded sx={{ color: mobiliTheme.palette.warning.main }} />
            )}
            {trailing === undefined && attendedStatus === "future" && (
              <InformingBadge
                projection={timeSlot?.informing}
                currentStart={timeSlot?.start}
                visitType={appointment?.address?.type}
                currentTherapistId={timeSlot?.user?.id}
                currentTherapistName={
                  [timeSlot?.user?.firstName, timeSlot?.user?.lastName]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
                size={16}
              />
            )}
          </Box>
        </Box>
      </div>

      {showReason && (
        <div className="pl-1.5 text-mini text-ink-muted">
          {/* A cancellation's card has no time row of its own any more, so the
              reason line is where the lost slot's original time survives. */}
          * {appointment.originalDate
            ? `${formatDateUI(appointment.originalDate)} ${formatTime(
                appointment.originalDate instanceof Date
                  ? appointment.originalDate.toISOString()
                  : appointment.originalDate
              )} – `
            : ""}
          {appointment.reason}
        </div>
      )}
    </div>
  );
};

export default TherapySlotRow;
